// fundAnalytics.ts — pure financial analytics over a fund's cached price/distribution/AGE
// history (GET /api/funds/:isin/history — see lib/types.ts's FundHistoryResponse, the
// frozen contract this all reads). No fetching, no DOM, no Preact here — HistorieView
// (views/HistorieView.tsx) is the only caller; everything below is independently
// unit-tested (fundAnalytics.test.ts) against hand-derived numbers.
//
// Calling convention: every function that operates over a *slice* of the price series
// (cagr, totalReturn, annualizedVolatility, growthOf, totalReturnGrowth) takes the same
// two optional trailing args, `fromDate?`/`toDate?` (inclusive ISO 'YYYY-MM-DD' bounds) —
// that pair IS the "window" the task spec refers to for those functions; cagr's own
// signature is the canonical shape the others match. Omitting both uses the fund's full
// cached history. Dates are compared as plain strings throughout (never parsed into a
// Date for comparison) — 'YYYY-MM-DD' sorts lexicographically exactly as it sorts
// chronologically, so this is exact, not an approximation.
import type { FundHistoryAgeReport, FundHistoryDistribution, FundHistoryPricePoint } from './types.js';

export type PricePoint = FundHistoryPricePoint;
export type DistributionPoint = FundHistoryDistribution;
export type AgeReport = FundHistoryAgeReport;

const MS_PER_DAY = 86_400_000;
const TRADING_DAYS_PER_YEAR = 252;
/** Average calendar days per year over a 4-year (1 leap-day) cycle — the standard
 * annualization divisor for CAGR-style calculations, so a multi-year window's exponent
 * isn't biased by whether it happens to straddle a leap day or not. */
const DAYS_PER_YEAR = 365.25;

function parseIsoUtcMs(dateIso: string): number {
  const [y, m, d] = dateIso.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function daysBetweenIso(fromIso: string, toIso: string): number {
  return (parseIsoUtcMs(toIso) - parseIsoUtcMs(fromIso)) / MS_PER_DAY;
}

/** Adds N calendar years to an ISO date, keeping month/day (JS `Date` itself normalises
 * the Feb-29-in-a-non-leap-target-year edge case by rolling into March, which is the
 * standard, unsurprising behaviour here). Exported: HistorieView reuses this exact
 * calendar-year arithmetic to turn a "1J/3J/5J/10J" range chip into a `fromDate` bound,
 * so the range chips line up exactly with what rollingAnnualizedReturns/CAGR windows mean. */
export function addYearsIso(dateIso: string, years: number): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  const dt = new Date(Date.UTC((y ?? 1970) + years, (m ?? 1) - 1, d ?? 1));
  return dt.toISOString().slice(0, 10);
}

/** Inclusive date-range filter shared by every windowed function below. `prices` is
 * assumed ascending (the API contract's own guarantee), so the result stays ascending too.
 * Exported for HistorieView's own use (e.g. slicing prices for the Kurs chart / filtering
 * distribution markers to the visible range) so there's exactly one date-window filter
 * in the codebase. */
export function sliceByWindow(prices: PricePoint[], fromDate?: string, toDate?: string): PricePoint[] {
  return prices.filter((p) => (!fromDate || p.date >= fromDate) && (!toDate || p.date <= toDate));
}

function mean(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mid = Math.floor(n / 2);
  return n % 2 !== 0 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** ((last/first)^(365.25/days)) − 1 over the (optionally date-sliced) window. `null`
 * when there's fewer than 2 prices in the window, or the window spans <= 0 days (can't
 * annualize a single point / same-day span). */
export function cagr(prices: PricePoint[], fromDate?: string, toDate?: string): number | null {
  const slice = sliceByWindow(prices, fromDate, toDate);
  if (slice.length < 2) return null;
  const first = slice[0]!;
  const last = slice[slice.length - 1]!;
  const days = daysBetweenIso(first.date, last.date);
  if (days <= 0 || first.closeEur <= 0) return null;
  return Math.pow(last.closeEur / first.closeEur, DAYS_PER_YEAR / days) - 1;
}

/** Simple (non-annualized) total return over the window: last/first − 1. */
export function totalReturn(prices: PricePoint[], fromDate?: string, toDate?: string): number | null {
  const slice = sliceByWindow(prices, fromDate, toDate);
  if (slice.length < 2) return null;
  const first = slice[0]!;
  const last = slice[slice.length - 1]!;
  if (first.closeEur <= 0) return null;
  return last.closeEur / first.closeEur - 1;
}

/** Annualized volatility: sample stdev (n−1) of daily log-returns × √252. `null` when
 * the window has fewer than 3 prices (need >= 2 daily returns for a sample stdev). */
export function annualizedVolatility(prices: PricePoint[], fromDate?: string, toDate?: string): number | null {
  const slice = sliceByWindow(prices, fromDate, toDate);
  if (slice.length < 3) return null;
  const logReturns: number[] = [];
  for (let i = 1; i < slice.length; i++) {
    const prev = slice[i - 1]!.closeEur;
    const cur = slice[i]!.closeEur;
    if (prev <= 0 || cur <= 0) continue;
    logReturns.push(Math.log(cur / prev));
  }
  if (logReturns.length < 2) return null;
  const m = mean(logReturns);
  const variance = logReturns.reduce((sum, r) => sum + (r - m) ** 2, 0) / (logReturns.length - 1);
  return Math.sqrt(variance) * Math.sqrt(TRADING_DAYS_PER_YEAR);
}

export interface DrawdownPoint {
  date: string;
  /** close/runningMax − 1, always <= 0. */
  value: number;
}

export interface DrawdownResult {
  series: DrawdownPoint[];
  /** The most negative value in `series` (0 if there is no drawdown at all / no data). */
  maxDrawdown: number;
}

/** Underwater curve over the FULL series (no windowing — the max-drawdown headline
 * figure is meant to reflect the whole cached history; callers wanting a windowed view
 * can slice `prices` themselves before calling, same as every other function here). */
export function drawdownSeries(prices: PricePoint[]): DrawdownResult {
  if (prices.length === 0) return { series: [], maxDrawdown: 0 };
  let runningMax = -Infinity;
  let maxDrawdown = 0;
  const series: DrawdownPoint[] = prices.map((p) => {
    if (p.closeEur > runningMax) runningMax = p.closeEur;
    const value = runningMax > 0 ? p.closeEur / runningMax - 1 : 0;
    if (value < maxDrawdown) maxDrawdown = value;
    return { date: p.date, value };
  });
  return { series, maxDrawdown };
}

export interface RollingReturnPoint {
  /** The window's START date. */
  date: string;
  annualizedReturn: number;
}

export interface RollingReturnsResult {
  series: RollingReturnPoint[];
  best: number | null;
  worst: number | null;
  median: number | null;
  avg: number | null;
}

/**
 * For every start date that has a full `years`-long window ahead of it (start + `years`
 * calendar years falls on or before the last cached price), the annualized return of
 * that window — i.e. a rolling p.a. series, the "headline" view per the task spec.
 * Windows use a two-pointer scan: target dates are monotonically increasing with the
 * start index (prices are ascending), so the end-pointer only ever moves forward — O(n).
 */
export function rollingAnnualizedReturns(prices: PricePoint[], years: number): RollingReturnsResult {
  const empty: RollingReturnsResult = { series: [], best: null, worst: null, median: null, avg: null };
  if (prices.length < 2 || years <= 0) return empty;

  const series: RollingReturnPoint[] = [];
  let j = 0;
  for (let i = 0; i < prices.length; i++) {
    const start = prices[i]!;
    const targetDate = addYearsIso(start.date, years);
    if (j < i) j = i;
    while (j < prices.length && prices[j]!.date < targetDate) j++;
    if (j >= prices.length) break; // no start from here on can have a full window either
    const end = prices[j]!;
    const days = daysBetweenIso(start.date, end.date);
    if (days <= 0 || start.closeEur <= 0) continue;
    const annualizedReturn = Math.pow(end.closeEur / start.closeEur, DAYS_PER_YEAR / days) - 1;
    series.push({ date: start.date, annualizedReturn });
  }

  if (series.length === 0) return empty;
  const values = series.map((p) => p.annualizedReturn);
  return {
    series,
    best: Math.max(...values),
    worst: Math.min(...values),
    median: median(values),
    avg: mean(values),
  };
}

export interface CalendarYearReturn {
  year: number;
  /** last close of that calendar year (as covered by cached data) / first close − 1. */
  return: number;
  /** True for an edge year whose covered data does not reach the full calendar year —
   * inception mid-year (first year) or "today" mid-year / cache not yet refreshed
   * (last year) — so the return is not comparable to a genuine full calendar year. */
  partial: boolean;
}

/** Trading days near the Jan 1 / Dec 31 boundary still count as "the full year" — New
 * Year's Day itself is never a trading day, so the first trading day of a normal year
 * already lands a few days into January (and the last a few days before Dec 31). Only
 * a gap noticeably larger than that (fund inception, or "now" being genuinely mid-year)
 * should be flagged partial. */
const YEAR_EDGE_TOLERANCE_DAYS = 6;

/** Per calendar year: first-trading-day -> last-trading-day close return, with the
 * dataset's own first/last calendar year flagged `partial` when its covered span doesn't
 * reach the year's edge (see YEAR_EDGE_TOLERANCE_DAYS). Not windowed — always the full
 * cached history, same rationale as drawdownSeries. */
export function calendarYearReturns(prices: PricePoint[]): CalendarYearReturn[] {
  if (prices.length === 0) return [];

  const byYear = new Map<number, PricePoint[]>();
  for (const p of prices) {
    const year = Number(p.date.slice(0, 4));
    const list = byYear.get(year);
    if (list) list.push(p);
    else byYear.set(year, [p]);
  }

  const years = Array.from(byYear.keys()).sort((a, b) => a - b);
  const firstYear = years[0]!;
  const lastYear = years[years.length - 1]!;

  return years.map((year) => {
    const points = byYear.get(year)!;
    const first = points[0]!;
    const last = points[points.length - 1]!;
    const ret = points.length < 2 || first.closeEur <= 0 ? 0 : last.closeEur / first.closeEur - 1;

    let partial = false;
    if (year === firstYear) {
      const jan1 = `${year}-01-01`;
      partial = daysBetweenIso(jan1, first.date) > YEAR_EDGE_TOLERANCE_DAYS;
    }
    if (year === lastYear) {
      const dec31 = `${year}-12-31`;
      partial = partial || daysBetweenIso(last.date, dec31) > YEAR_EDGE_TOLERANCE_DAYS;
    }
    if (points.length < 2) partial = true;

    return { year, return: ret, partial };
  });
}

export interface GrowthPoint {
  date: string;
  valueCents: number;
}

/** Indexed value series: `amountCents` invested at the window's first close, tracked at
 * the raw closing price (no reinvestment — see totalReturnGrowth for that). For an
 * accumulating (acc) fund this already IS the total-return curve (distributions are
 * retained in the NAV); it's only a price-only view for a distributing (dist) fund. */
export function growthOf(amountCents: number, prices: PricePoint[], fromDate?: string, toDate?: string): GrowthPoint[] {
  const slice = sliceByWindow(prices, fromDate, toDate);
  if (slice.length === 0) return [];
  const base = slice[0]!.closeEur;
  if (base <= 0) return [];
  return slice.map((p) => ({ date: p.date, valueCents: Math.round((amountCents * p.closeEur) / base) }));
}

/**
 * Same indexed-growth idea as growthOf, but each GROSS (pre-tax/brutto) distribution is
 * reinvested at its ex-date close — i.e. a pre-tax total-return curve. Only meaningful
 * for distributing funds: for an accumulating fund the price series is already total
 * return (retained internally), so calling this on an acc fund's data would silently
 * double-count — HistorieView only offers the "inkl. Ausschüttungen" toggle for dist funds.
 * Reinvestment mechanics: starts with `units = amountCents / firstClose`; at the first
 * price point on/after each distribution's ex-date, adds
 * `units * grossPerUnitEur / closeAtThatPoint` more units (matches the ex-date close when
 * the ex-date itself is a cached trading day, which it always should be for real data).
 */
export function totalReturnGrowth(
  amountCents: number,
  prices: PricePoint[],
  distributions: DistributionPoint[],
  fromDate?: string,
  toDate?: string,
): GrowthPoint[] {
  const slice = sliceByWindow(prices, fromDate, toDate);
  if (slice.length === 0) return [];
  const first = slice[0]!;
  const last = slice[slice.length - 1]!;
  if (first.closeEur <= 0) return [];

  const relevant = distributions
    .filter((d) => d.exDate >= first.date && d.exDate <= last.date)
    .sort((a, b) => a.exDate.localeCompare(b.exDate));

  let units = amountCents / 100 / first.closeEur;
  let distIdx = 0;
  const out: GrowthPoint[] = [];
  for (const p of slice) {
    while (distIdx < relevant.length && relevant[distIdx]!.exDate <= p.date) {
      const d = relevant[distIdx]!;
      if (p.closeEur > 0) units += (units * d.grossPerUnitEur) / p.closeEur;
      distIdx++;
    }
    out.push({ date: p.date, valueCents: Math.round(units * p.closeEur * 100) });
  }
  return out;
}

/** Sum of gross distributions with exDate in (asOfDate − 365 days, asOfDate] / last
 * close. `asOfDate` defaults to today (real-world usage); tests pass it explicitly for a
 * deterministic result. `0` (not `null`) when there were genuinely no distributions in
 * the trailing 12 months — that's a real fact, not missing data. `null` only when
 * `lastCloseEur` itself is missing/non-positive (can't divide). */
export function distributionYieldTtm(
  distributions: DistributionPoint[],
  lastCloseEur: number | null,
  asOfDate: string = new Date().toISOString().slice(0, 10),
): number | null {
  if (lastCloseEur == null || lastCloseEur <= 0) return null;
  const windowStart = addYearsIso(asOfDate, -1);
  const sum = distributions
    .filter((d) => d.exDate > windowStart && d.exDate <= asOfDate)
    .reduce((s, d) => s + d.grossPerUnitEur, 0);
  return sum / lastCloseEur;
}

/** Total span the cached price history covers, in years (365.25-day convention, same as
 * cagr/rollingAnnualizedReturns). 0 for an empty or single-point series. */
export function historySpanYears(prices: PricePoint[]): number {
  if (prices.length < 2) return 0;
  const days = daysBetweenIso(prices[0]!.date, prices[prices.length - 1]!.date);
  return Math.max(0, days) / DAYS_PER_YEAR;
}

/** ~1 month of slack so a fund with, say, 4y11m of cached history still offers the "5J"
 * window — real trading calendars essentially never land exactly on a round year
 * boundary. */
const WINDOW_TOLERANCE_YEARS = 1 / 12;

/** Filters a list of candidate holding periods (in years) down to the ones the cached
 * history can actually support — HistorieView uses this for BOTH the CAGR tile strip
 * and the time-series range chips, so a fund with 6 years of data never offers a fake
 * "10J" number/window (D-013-style honesty: never fabricate what isn't there). */
export function availableWindowYears(prices: PricePoint[], candidates: number[]): number[] {
  const span = historySpanYears(prices);
  return candidates.filter((y) => span >= y - WINDOW_TOLERANCE_YEARS);
}

export interface DistributionYearPoint {
  year: number;
  totalPerUnitEur: number;
}

/** Gross distributions summed per calendar year of their ex-date — the "Ausschüttungen/
 * Jahr" bar chart (dist funds only). */
export function distributionsByYear(distributions: DistributionPoint[]): DistributionYearPoint[] {
  const byYear = new Map<number, number>();
  for (const d of distributions) {
    const year = Number(d.exDate.slice(0, 4));
    byYear.set(year, (byYear.get(year) ?? 0) + d.grossPerUnitEur);
  }
  return Array.from(byYear.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, totalPerUnitEur]) => ({ year, totalPerUnitEur }));
}

export interface AgeYearPoint {
  year: number;
  totalPerUnitEur: number;
}

/** AGE (ausschüttungsgleiche Erträge) per unit, grouped by fiscal year, for the bar
 * chart. Groups by `fiscalYearEnd`'s year when present (the fund's own declared fiscal
 * year); falls back to the `meldungDate`'s year for the rare report that's missing it,
 * rather than dropping the data point. Sums defensively (there should be one report per
 * fiscal year, but this stays correct if a source ever supplies more than one). */
export function ageByYear(age: AgeReport[]): AgeYearPoint[] {
  const byYear = new Map<number, number>();
  for (const r of age) {
    const year = Number((r.fiscalYearEnd ?? r.meldungDate).slice(0, 4));
    byYear.set(year, (byYear.get(year) ?? 0) + r.agePerUnitEur);
  }
  return Array.from(byYear.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, totalPerUnitEur]) => ({ year, totalPerUnitEur }));
}
